import { AsyncPipe, DatePipe, NgTemplateOutlet } from '@angular/common';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { TimeAgoModule } from '@callrail/looky/ui';
import { VerticalLabelPipe } from '@callrail/looky/vertical-label';

import { appNoticeLinks } from '../../../constants/app-notice-card-links';
import {
  installMethodPath,
  resolveInstallMethod,
} from '../../../constants/install-methods';
import { AgencyModel } from '../../../models/agency.model';
import { AppNotice } from '../../../models/app-notice.model';
import { CompanyModel } from '../../../models/company.model';
import { DirectivesModule } from '../../../directives/directives.module';
import { FlipperService } from '../../../services/flipper/flipper.service';
import { UserModel } from '../../../models/user.model';
import { RoutesService } from '../../../services/routes/routes.service';
import { BillingService } from '../../../services/billing/billing.service';
import { AppNoticesService } from '../../../services/app-notices/app-notices.service';
import { Subject, takeUntil } from 'rxjs';
import dayjs from 'dayjs';

const NOTICE_TITLES: Partial<Record<string, string>> = {
  annual_plans_reminder: 'Annual plan renewal reminder',
  js_not_installed: 'JavaScript snippet not installed',
  account_disabled: 'Account disabled',
  account_closing: 'Account closing',
  trial_pending: 'Trial pending',
  trial: 'In trial',
  lc_trial: 'In trial',
  forms_legacy_upgrade_required: 'Forms upgrade required',
  forms_lite_upgrade_required: 'Forms upgrade required',
  form_capture_limit: 'Form submission limit reached',
  billing: 'Payment failed',
  manual_invoice_late: 'Invoice past due',
  no_trackers: 'No active numbers',
  api_v3: 'API v3 now available',
  no_forms_product: 'Try form tracking',
  risky_swap: 'Numbers swapping too fast',
  number_not_swapping: 'Number not swapping',
  snippet_installation: '',
  redundant_plans: 'Redundant plans detected',
  tendlc_registration__high: '10DLC registration required',
  tendlc_registration__high__admin: '10DLC registration required',
  tendlc_registration__mid: '10DLC registration warning',
  tendlc_registration__mid__admin: '10DLC registration warning',
  tendlc_registration__low: '10DLC registration reminder',
  tendlc_registration__low__admin: '10DLC registration reminder',
  ten_dlc_registered: 'Registered for business texting',
  ten_dlc_registered_voice_assist: 'Registered for business texting',
  ten_dlc_registered_trial_ended: "You're out of your trial period",
  ten_dlc_registered_trial_ended_voice_assist:
    "You're out of your trial period",
};

@Component({
  selector: 'core-app-notice-message',
  templateUrl: './app-notice-message.component.html',
  styleUrls: ['./app-notice-message.component.scss'],
  imports: [
    AsyncPipe,
    DatePipe,
    NgTemplateOutlet,
    DirectivesModule,
    VerticalLabelPipe,
    TimeAgoModule,
  ],
})
export class AppNoticeMessageComponent implements OnInit, OnDestroy {
  @Input() public appNotice: AppNotice;
  @Input() public agency: AgencyModel;
  @Input() public company: CompanyModel;
  @Input() public user: UserModel;
  @Input() public withAlertTitle = false;
  @Input() public withPadding = false;
  @Input() public withMockLinks = false;
  @Input() public withMessageVariant = false;

  public routes;
  public showCCLinkToPending$ = this.flipperService.flipperEnabled(
    'show_cc_link_to_pending'
  );
  public appNoticeLinks = appNoticeLinks;

  public annualRenewalDate: string;

  private readonly destroy$ = new Subject<void>();

  constructor(
    private routesService: RoutesService,
    public flipperService: FlipperService,
    public billingService: BillingService,
    private appNoticesService: AppNoticesService
  ) {
    this.routes = this.routesService.routes;
  }

  public ngOnInit() {
    if (this.appNotice?.name === 'annual_plans_reminder') {
      this.addAnnualRenewalDate();
    }
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public get noticeTitle(): string {
    if (this.appNotice?.category === 'general') return '';
    if (this.appNotice?.category === 'integration') {
      return this.appNotice.name === 'failed'
        ? 'Integration failed'
        : 'Integration incomplete';
    }
    return NOTICE_TITLES[this.appNotice?.name] ?? 'Account alert';
  }

  public markViewed() {
    this.appNoticesService.markViewed(this.appNotice.id);
  }

  /**
   * companyId is the notice's own idea of the relevant company (e.g.
   * appNotice.meta?.company_id, or the js_not_installed fallback to
   * agency.first_company_id) — only trust the detected recommendation when
   * it actually matches the resolved company on this component.
   */
  public installUrl(companyId?: string): string {
    const matchesResolvedCompany =
      !!companyId && companyId === this.company?.id;
    const flagEnabled =
      matchesResolvedCompany &&
      this.flipperService.eagerlyEnabled('recommend_install_method');
    const method = resolveInstallMethod(this.company, this.agency, flagEnabled);
    return installMethodPath(method, { agencyId: this.agency.id, companyId });
  }

  private addAnnualRenewalDate(): void {
    this.billingService.billingSubscription$
      .pipe(takeUntil(this.destroy$))
      .subscribe((subscription) => {
        this.annualRenewalDate = dayjs(subscription.annualRenewalDate).format(
          'MMM DD'
        );
      });
  }
}
